import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import { api } from '../../shared/api/api';
import { useApiData } from '../../shared/hooks/useFetch';
import LimiteErreur from '../../shared/components/LimiteErreur';
import HistoriqueSafranChart from '../../shared/charts/HistoriqueSafranChart';
import { formatJourMois, formatJJMM, formatNombre, quantile } from '../../shared/utils/eau3mois';

const AUCUN_PARAMETRE = {};

/**
 * Eau à 3 mois / Historique besoin en eau culture — port de 07_historique_safran.R :
 * panneau 2x2 du cumul du besoin en eau de la plante à l'échelle du territoire
 * (maïs précoce / tardif x semis 05/05 / 15/04), une courbe par année SAFRAN
 * observée, l'année en cours en noir pointillé (campagne arrêtée à la dernière
 * date observée). Puis la place de l'année en cours parmi les autres, à date
 * comparable. Données observées uniquement : aucun paramètre dans la sidebar.
 */
export default function HistoriqueBesoinEau() {
  const { idExploit } = useOAD();
  const { data, loading, error } = useApiData(api.getHistoriqueSafran, AUCUN_PARAMETRE);

  // Où se place l'année en cours ? Comparaison À DATE COMPARABLE : le cumul de chaque
  // année est lu à la dernière date observée de l'année en cours, sans quoi on
  // comparerait une campagne partielle à des campagnes complètes (§ 4 du script R).
  // Rang 1 = année la plus demandeuse.
  const lignes = React.useMemo(() => {
    if (!data) return [];
    return data.panneaux.map((p) => {
      const annees = Object.keys(p.courbes);
      const jour = p.anneeMiseEnAvant
        ? p.courbes[p.anneeMiseEnAvant].length - 1
        : Math.max(...annees.map((a) => p.courbes[a].length - 1));
      const cumuls = annees.map((a) => ({ annee: Number(a), cumul: p.courbes[a][Math.min(jour, p.courbes[a].length - 1)] }));
      const tries = cumuls.map((c) => c.cumul).sort((x, y) => x - y);
      const enAvant = p.anneeMiseEnAvant ? cumuls.find((c) => c.annee === p.anneeMiseEnAvant) : null;
      return {
        label: p.label, n: cumuls.length, min: tries[0], mediane: quantile(tries, 0.5), max: tries[tries.length - 1],
        cumulEnAvant: enAvant?.cumul ?? null,
        rang: enAvant ? 1 + cumuls.filter((c) => c.cumul > enAvant.cumul).length : null,
      };
    });
  }, [data]);

  const annees = data
    ? [...new Set(data.panneaux.flatMap((p) => Object.keys(p.courbes).map(Number)))].sort((a, b) => a - b)
    : [];
  const finObserve = data?.panneaux.find((p) => p.finObserve)?.finObserve;
  const nonProlongees = data ? [...new Set(data.panneaux.flatMap((p) => p.nonProlongees))] : [];

  return (
    <div className="oad-contenu">
      {/* Pas de paramètres : seulement la navigation entre les pages. */}
      <SidebarParametres niveau1="annee-en-cours" niveau2="eau-3-mois" niveau3="historique" idExploit={idExploit} />

      <main className="oad-cards">
        <section className="oad-card">
          <h2 className="tdb-section-titre">Cumul du besoin en eau — historique SAFRAN</h2>
          {error && <p className="tdb-erreur">{error}</p>}
          {loading && <p className="tdb-chargement">Chargement…</p>}
          {data && (
            <>
              <p className="ig-tableau-sous-titre" style={{ color: '#555', fontSize: 13 }}>
                Météo SAFRAN observée | Bretagne | {formatNombre(data.info.surfaceHa, 0)} ha de maïs (RPG)
                <br />
                {annees.length} années observées : {annees[0]} à {annees[annees.length - 1]}
                {finObserve && ` | ${data.anneeMiseEnAvant} en noir pointillé, campagne arrêtée au ${formatJourMois(finObserve)}`}
              </p>
              <LimiteErreur cle="histo-safran">
                <HistoriqueSafranChart panneaux={data.panneaux} anneeMiseEnAvant={data.anneeMiseEnAvant} />
              </LimiteErreur>
              <p style={{ fontSize: 13, color: '#555', textAlign: 'right' }}>
                Besoin en eau de la plante (évapotranspiration), non l'eau à prélever.
                {nonProlongees.length > 0 &&
                  ` La campagne ${nonProlongees.join(', ')} s'arrête avant la fin de la fenêtre (série interrompue en plein cycle, non prolongée).`}
              </p>

              <p className="ig-tableau-sous-titre" style={{ marginTop: 12 }}>
                Cumul au {finObserve ? formatJJMM(finObserve) : 'dernier jour'}, en millions de m³ (rang 1 = année la plus demandeuse)
              </p>
              <table className="oad-data-table oad-table-centered">
                <thead>
                  <tr>
                    <th>Combinaison</th><th>Min</th><th>Médiane</th><th>Max</th>
                    <th>{data.anneeMiseEnAvant}</th><th>Rang {data.anneeMiseEnAvant}</th><th>Sur (années)</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => (
                    <tr key={l.label}>
                      <td>{l.label}</td>
                      <td>{formatNombre(l.min, 1)}</td>
                      <td>{formatNombre(l.mediane, 1)}</td>
                      <td>{formatNombre(l.max, 1)}</td>
                      <td>{l.cumulEnAvant == null ? '—' : formatNombre(l.cumulEnAvant, 1)}</td>
                      <td>{l.rang ?? '—'}</td>
                      <td>{l.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
